import React from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import {
  HouseNightlyTripIndividualDto,
  SeasonEnum,
} from '../../../../../../../../../../../lib/graphql'
import { HTLabel } from '../../../../../../../../../../atoms'
import { ControlledTextField } from '../../../../../../../../../../forms'
import PriceSeasonColorTag from '../../../../../../../../shared/components/seasonal-busyness-calendar/components/tag/price-season-color-tag/PriceSeasonColorTag'
import { formatMoney } from '../../../../../../../../shared/utils/format/format-money/format-money'
import { HouseNightlyTripIndividualFieldProps } from '../../../../form-container/HouseNightlyTripIndividualFormContainer'

type HouseNightlyTripIndividualSeasonalPriceFieldProps =
  HouseNightlyTripIndividualFieldProps & {
    name:
      | 'sharedBedroom.highPrice'
      | 'sharedBedroom.lowPrice'
      | 'sharedBedroom.mediumPrice'
      | 'singleBedroom.highPrice'
      | 'singleBedroom.mediumPrice'
      | 'singleBedroom.lowPrice'
  }
export const HouseNightlyTripIndividualSeasonalPriceField = ({
  control,
  currencyArea,
  isFieldDisabled,
  name,
}: HouseNightlyTripIndividualSeasonalPriceFieldProps) => {
  const fieldProps = {
    disabled: isFieldDisabled,
    control,
    type: 'number',
    inline: true,
    rules: {},
    placeholder: formatMoney({ value: 0, currency: currencyArea.currency }),
  }

  const { setValue } = useFormContext<HouseNightlyTripIndividualDto>()

  const priceName = name.split('.')[1]

  return (
    <>
      <ControlledTextField
        {...fieldProps}
        name={`options.seasonalPrices.0.${name}`}
        formatValue={(value) =>
          formatMoney({ value, currency: currencyArea.currency })
        }
        placeholder={formatMoney({ value: 0, currency: currencyArea.currency })}
        onReset={() =>
          setValue(`options.seasonalPrices.0.${name}`, null, {
            shouldDirty: true,
          })
        }
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PriceSeasonColorTag
              season={
                priceName === 'highPrice'
                  ? SeasonEnum.HIGH
                  : priceName === 'lowPrice'
                    ? SeasonEnum.LOW
                    : SeasonEnum.MEDIUM
              }
            />

            <div>
              <HouseNightlyTripIndividualSeasonalPriceFieldLabel name={name} />

              <span style={{ marginLeft: 5 }}>
                <HTLabel />
              </span>
            </div>
          </div>
        }
      />
    </>
  )
}

const HouseNightlyTripIndividualSeasonalPriceFieldLabel = ({
  name,
}: Pick<HouseNightlyTripIndividualSeasonalPriceFieldProps, 'name'>) => {
  switch (name) {
    case 'singleBedroom.lowPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.singleBedroom.lowPrice'
          defaultMessage='<bold>Prix nuitée en chambre individuelle,{br} par personne</bold> - Basse saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )

    case 'sharedBedroom.lowPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.sharedBedroom.lowPrice'
          defaultMessage='<bold>Prix nuitée en chambre partagée,{br} par personne</bold> - Basse saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )

    case 'singleBedroom.mediumPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.singleBedroom.mediumPrice'
          defaultMessage='<bold>Prix nuitée en chambre individuelle,{br} par personne</bold> - Moyenne saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )

    case 'sharedBedroom.mediumPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.sharedBedroom.mediumPrice'
          defaultMessage='<bold>Prix nuitée en chambre partagée,{br} par personne</bold> - Moyenne saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )

    case 'singleBedroom.highPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.singleBedroom.highPrice'
          defaultMessage='<bold>Prix nuitée en chambre individuelle,{br} par personne</bold> - Haute saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )

    case 'sharedBedroom.highPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripIndividualFormFields.options.seasonalPrices.0.sharedBedroom.highPrice'
          defaultMessage='<bold>Prix nuitée en chambre partagée,{br} par personne</bold> - Haute saison'
          values={{ bold: (content) => <b>{content}</b>, br: <br /> }}
        />
      )
  }
}
